// src/app/shared/selection-button/selection-button.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextSelectionService } from 'src/app/services/text-selection/text-selection.service';
import { ApiService } from 'src/app/services/api/api.service';
import { ContextService } from 'src/app/services/context/context.service';
import { MessageService } from 'primeng/api';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-selection-button',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, SelectModule, ProgressSpinnerModule],
  template: `
    <div class="selection-button" *ngIf="hasSelection"
      [style.left.px]="position.x"
      [style.top.px]="position.y">
      <div class="flex items-center gap-1">
        <button pButton icon="pi pi-file-edit"
          label="Ajouter à ma note"
          (click)="addToNote()"
          class="p-button-sm p-button-rounded !bg-amber-400 !border-amber-400">
        </button>
        <button pButton icon="pi pi-bolt"
          label="Envoyer à un agent"
          (click)="toggleAgentDropdown()"
          class="p-button-sm p-button-rounded p-button-help">
        </button>
      </div>
      <!-- Agent dropdown -->
      <div *ngIf="showAgentDropdown" class="agent-dropdown mt-2">
        <div class="flex items-center gap-2">
          <p-select
            [options]="agents"
            [(ngModel)]="selectedAgentId"
            optionLabel="name"
            optionValue="id"
            placeholder="Sélectionner un agent"
            (onChange)="onAgentSelected($event)"
            styleClass="w-full"
            [loading]="isLoadingAgents"
            [disabled]="isSendingToAgent">
          </p-select>
          <p-progressSpinner *ngIf="isSendingToAgent" strokeWidth="3" fill="transparent" animationDuration=".5s" styleClass="!w-5 !h-5"></p-progressSpinner>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .selection-button {
      position: absolute;
      z-index: 9999;
      transform: translateX(-50%);
      transition: all 0.2s ease;
    }
    .agent-dropdown {
      background: var(--surface-card, white);
      border-radius: 6px;
      padding: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      min-width: 200px;
    }
  `]
})
export class SelectionButtonComponent implements OnInit, OnDestroy {
  hasSelection: boolean = false;
  position = { x: 0, y: 0 };
  showAgentDropdown: boolean = false;
  agents: { name: string; id: number }[] = [];
  selectedAgentId: number | null = null;
  isLoadingAgents: boolean = false;
  isSendingToAgent: boolean = false;

  private destroy$ = new Subject<void>();
  private selectedText: string = '';
  private context: any = null;

  constructor(
    private textSelectionService: TextSelectionService,
    private apiService: ApiService,
    private contextService: ContextService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.textSelectionService.hasSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasSelection => {
        this.hasSelection = hasSelection;
        if (!hasSelection) {
          this.showAgentDropdown = false;
        }
      });

    this.textSelectionService.selectionPosition$
      .pipe(takeUntil(this.destroy$))
      .subscribe(position => {
        this.position = position;
      });

    this.textSelectionService.selectedText$
      .pipe(takeUntil(this.destroy$))
      .subscribe(text => {
        this.selectedText = text;
      });

    // Subscribe to context changes
    this.contextService.context$
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        this.context = context;
      });
  }

  addToNote() {
    this.textSelectionService.addSelectedTextToNote();
    window.getSelection()?.removeAllRanges();
    this.hasSelection = false;
    this.showAgentDropdown = false;
  }

  toggleAgentDropdown() {
    this.showAgentDropdown = !this.showAgentDropdown;
    if (this.showAgentDropdown && this.agents.length === 0) {
      this.loadAgents();
    }
  }

  loadAgents() {
    this.contextService.context$.pipe(take(1)).subscribe((context: any) => {
      const companyId = context?.base?.id;
      if (!companyId) {
        this.isLoadingAgents = false;
        return;
      }

      this.isLoadingAgents = true;
      this.apiService.getCompanyAgents(Number(companyId)).subscribe({
        next: (agents: any[]) => {
          this.agents = (agents || []).map((a: any) => ({ name: a.name, id: a.id }));
          this.isLoadingAgents = false;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
          this.isLoadingAgents = false;
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Erreur',
            detail: 'Impossible de charger les agents'
          });
        }
      });
    });
  }

  onAgentSelected(event: any) {
    if (!event.value || !this.selectedText) return;

    const companyId = this.context?.base?.id;
    if (!companyId) return;

    this.isSendingToAgent = true;
    this.apiService.autoRunAgent({
      company_id: companyId,
      agent_id: event.value,
      content: this.selectedText
    }).subscribe({
      next: () => {
        this.isSendingToAgent = false;
        this.messageService.add({
          key: 'tst',
          severity: 'success',
          summary: 'Succès',
          detail: 'Contenu envoyé à l\'agent'
        });
        window.getSelection()?.removeAllRanges();
        this.hasSelection = false;
        this.showAgentDropdown = false;
        this.selectedAgentId = null;
      },
      error: (error) => {
        this.isSendingToAgent = false;
        console.error('Error sending to agent:', error);
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Erreur',
          detail: error?.error?.message || 'Impossible d\'envoyer à l\'agent'
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}